import { ImportFieldDefinition, ColumnMapping } from "../DataImport.types";

/**
 * Automatically maps CSV column names to import field definitions.
 *
 * Matching priority:
 * 1. Exact match on field.key (case-insensitive)
 * 2. Exact match on field.label (case-insensitive)
 * 3. Match on any field.aliases entry (case-insensitive)
 *
 * Each CSV column is only assigned once (first match wins).
 */
export function autoMapColumns(
  fields: ImportFieldDefinition[],
  csvColumnNames: string[]
): ColumnMapping {
  const mapping: ColumnMapping = {};
  const usedColumns = new Set<string>();

  const normalize = (str: string) => str.toLowerCase().trim();

  const csvColumnsLower = csvColumnNames.map((col) => ({
    original: col,
    normalized: normalize(col),
  }));

  const findColumn = (matchValue: string): string | null => {
    const normalizedMatch = normalize(matchValue);
    const found = csvColumnsLower.find(
      (col) =>
        col.normalized === normalizedMatch && !usedColumns.has(col.original)
    );
    if (found) {
      usedColumns.add(found.original);
      return found.original;
    }
    return null;
  };

  for (const field of fields) {
    // 1. Try exact match on field key
    let matched = findColumn(field.key);

    // 2. Try exact match on field label
    if (!matched) {
      matched = findColumn(field.label);
    }

    // 3. Try aliases
    if (!matched && field.aliases) {
      for (const alias of field.aliases) {
        matched = findColumn(alias);
        if (matched) break;
      }
    }

    mapping[field.key] = matched;
  }

  return mapping;
}
